import type { ProfileCustomization } from '@perplexicon/api-client';
import type { AvatarConfig } from '@perplexicon/validation';

import { OrnateFrame } from '../../../components/index.js';
import { AvatarDisplay } from '../../profile/components/AvatarDisplay.js';
import { HeroThemeBackdrop } from '../../profile/components/HeroThemeBackdrop.js';
import { ProfileIdentity } from '../../profile/components/ProfileIdentity.js';
import { resolveCosmeticAsset } from '../../profile/profile-assets.js';
import { PreviewSideDecoration } from './PreviewSideDecoration.js';

/**
 * The Settings "Live Preview" (APP_SPEC §13.2) — a purpose-built preview for
 * the narrower Settings context, not `ProfileHero` shrunk down. It shows only
 * what this page can actually customize: avatar + frame, username, joined
 * date, selected title, background, side decoration. No stats, no gameplay
 * cards, no `actions` slot — those belong to the public profile only.
 *
 * Shares its background-art treatment with `ProfileHero` via
 * `HeroThemeBackdrop` and reuses `AvatarDisplay`/`ProfileIdentity` verbatim
 * (both were already stats-free), so the two surfaces stay materially
 * identical without forking the hero. Responds to `customization` changes
 * immediately — the caller passes the live draft, not the saved value.
 */
export interface ProfileAppearancePreviewProps {
  userId: string;
  username: string;
  avatar: AvatarConfig | null;
  customization: ProfileCustomization;
}

export function ProfileAppearancePreview({
  userId,
  username,
  avatar,
  customization,
}: ProfileAppearancePreviewProps) {
  const backgroundUrl = customization.heroBackgroundId
    ? resolveCosmeticAsset('heroBackground', customization.heroBackgroundId)
    : null;

  return (
    <OrnateFrame
      as="section"
      variant="hero"
      cornerSize={28}
      edgeThickness={4}
      aria-label={`${username} profile appearance preview`}
      className="overflow-hidden rounded-lg bg-parchment text-textPrimary shadow-2"
    >
      <HeroThemeBackdrop backgroundUrl={backgroundUrl} />
      <PreviewSideDecoration decorationId={customization.sideDecorationId} />

      <div className="relative z-[1] flex flex-col items-center gap-3 p-4 text-center tablet:flex-row tablet:items-center tablet:gap-6 tablet:p-6 tablet:text-left">
        <AvatarDisplay
          userId={userId}
          username={username}
          avatar={avatar}
          frameId={customization.avatarFrameId}
          size={104}
        />
        <div className="flex min-w-0 flex-col items-center tablet:items-start">
          <ProfileIdentity
            username={username}
            // No creation-date field exists on `CurrentUser` yet (deliberate —
            // adding one is a server change out of scope here); today's date
            // is a placeholder in the *real* editor too, preserved as-is.
            joinedAt={new Date().toISOString()}
            titleId={customization.selectedTitleId}
          />
        </div>
      </div>
    </OrnateFrame>
  );
}
